import React from 'react'
import { useAtomValue } from 'jotai'

import { Access } from '../../lib'
import * as state from '../../states'
import { TRevenueSpendAgg, ERevenueAgencyFakeType } from '../../types'
import { IUiTableConfig, UiTable } from '../ui'
import * as helpers from '../../helpers'
import dt from 'dayjs'
import { RevenueAgencyFakeTypeField } from '../revenueAgency'
import { REVENUE_AGENCY_FAKE_TYPE_COLORS_MAP } from '../../constants'
import { sortRevenueSpendItemsByAt } from './RevenueSpendByAccountSpendDetailsTable'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IRevenueSpendByTeamSpendDetailsTableProps = {
	items: TRevenueSpendAgg[];
	skip: number;
}

const NO_DATA_CELL: React.ReactNode = '-'

export const RevenueSpendByTeamSpendDetailsTable = (props: IRevenueSpendByTeamSpendDetailsTableProps) => {
	const {
		items = [],
		skip = 0
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const tableConfig: IUiTableConfig<TRevenueSpendAgg>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_id)
		},
		{
			key: 'at',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_date),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const at = rowData?.at ?? null
				return <>
					{dt.utc(at).isValid() ? dt.utc(at).format('L') : '-'}
				</>
			}
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_amount),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const formatValue = helpers.formatAmount(value)

				return <>{helpers.thousandSeparator(formatValue)}</>
			}
		},
		{
			key: 'percent',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_percent),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const formatValue = helpers.formatAmount(rowData.percent * 100)

				return <>{helpers.thousandSeparator(formatValue)}%</>
			}
		},
		{
			key: 'accountId',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_account),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const accountId = rowData?.accountId ?? null
				const account = rowData?.account ?? null
				const email = account?.email ?? null
				const auth = account?.auth ?? null

				const login = auth?.login ?? null
				return <>
					<strong>[id: {accountId}]</strong>
					&nbsp;
					{login ?? '?'}
				</>
			}
		},
		{
			key: 'account.extra.keitaroLogin',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_kt_group),
			rowCellFormatter: ({ rowData }) => {
				const extra = rowData.account?.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'agencyId',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_agency),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const agencyId = rowData?.agencyId ?? null
				const agency = rowData?.agency ?? null
				const name = agency?.name ?? null

				return <>
					<strong>[id: {agencyId}]</strong>
					&nbsp;
					{name ?? '?'}
				</>
			}
		},
		{
			key: 'fakeType',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_spend_details_table.column_fake_type),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				const valueFormat: ERevenueAgencyFakeType = rowData?.agency?.fakeType as any ?? ERevenueAgencyFakeType.None

				if (!rowData.agency) {
					return NO_DATA_CELL
				}

				return <div title={valueFormat}>
					<RevenueAgencyFakeTypeField
						value={valueFormat}
						rowData={rowData.agency}
						classNamesToAdd={REVENUE_AGENCY_FAKE_TYPE_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on RevenueAgencyFakeType field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</div>
			}
		}
	]

	const sortedItemsByDate = sortRevenueSpendItemsByAt(items)

	return <>
		<UiTable
			data={sortedItemsByDate}
			config={tableConfig}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper table-sticky-wrapper--top-60',
			// }}
		/>
	</>
}